import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, SITE } from "@/data/index";

/* @section: header */
function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background/95 shadow-sm" : "bg-transparent"
      }`}
      style={{ borderBottom: scrolled ? "1px solid var(--border)" : "none" }}
    >
      <div className="container mx-auto px-6 h-18 flex items-center justify-between" style={{ height: "72px" }}>
        {/* Logo */}
        <Link to="/" className="flex flex-col leading-none group">
          <span
            className="text-xl font-semibold tracking-widest text-foreground"
            style={{ fontFamily: "'Noto Serif JP', serif", letterSpacing: "0.12em" }}
          >
            HR Progress
          </span>
          <span className="text-xs text-muted-foreground tracking-widest mt-0.5" style={{ letterSpacing: "0.15em" }}>
            COACHING & CONSULTING
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-5">
          {NAV_LINKS.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm tracking-wider transition-colors duration-200 relative pb-1 ${
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
                style={{ letterSpacing: "0.08em" }}
              >
                {link.label}
                {isActive && (
                  <motion.span
                    layoutId="nav-underline"
                    className="absolute bottom-0 left-0 right-0 h-px bg-primary"
                  />
                )}
              </Link>
            );
          })}
          <Link
            to="/contact"
            className="ml-2 px-5 py-2 bg-primary text-primary-foreground text-sm tracking-wider rounded-sm hover:opacity-90 transition-opacity"
            style={{ letterSpacing: "0.08em" }}
          >
            お問い合わせ
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-foreground p-1"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="メニュー"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-background border-t border-border px-6 py-6 flex flex-col gap-5"
          >
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-base text-foreground tracking-wider"
                style={{ letterSpacing: "0.08em" }}
              >
                {link.label}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

/* @section: footer */
function Footer() {
  return (
    <footer className="border-t border-border bg-background">
      <div className="container mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row justify-between items-start gap-8">
          <div>
            <div
              className="text-lg font-semibold tracking-widest text-foreground mb-1"
              style={{ fontFamily: "'Noto Serif JP', serif", letterSpacing: "0.12em" }}
            >
              HR Progress
            </div>
            <div className="text-xs text-muted-foreground tracking-widest mb-4" style={{ letterSpacing: "0.15em" }}>
              COACHING & CONSULTING
            </div>
            <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
              自分らしい強みを見つけて、<br />成果に繋げる。
            </p>
          </div>
          <nav className="flex flex-col gap-3">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-sm text-muted-foreground hover:text-foreground tracking-wider transition-colors"
                style={{ letterSpacing: "0.06em" }}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="flex flex-col gap-2">
            <a
              href={`mailto:${SITE.email}`}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {SITE.email}
            </a>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-border flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} HR Progress Coaching Office. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

/* @section: layout-root */
export default function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-[72px]">{children}</main>
      <Footer />
    </div>
  );
}
